import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { VerdictBadge, VERDICTS } from "@/components/RiskBadge";
import {
  ArrowLeft, RefreshCw, GitCompareArrows, KeyRound, Hourglass, ShieldCheck, Copy, Check, PlayCircle, FileText,
} from "lucide-react";

export default function SessionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [s, setS] = useState(null);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () =>
    api.get(`/sessions/${id}`).then(({ data }) => setS(data)).catch(() => setErr("Session not found"));

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const copy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(""), 1500);
  };

  const requestRescan = async () => {
    const latest = s.scans[s.scans.length - 1];
    if (!latest) return;
    setBusy(true);
    try {
      await api.post(`/scans/${latest.id}/rescan`);
      await load();
    } finally {
      setBusy(false);
    }
  };

  if (err) return <div className="text-slate-400">{err}</div>;
  if (!s) return <div className="text-slate-500">Loading…</div>;

  // Merge scans + pending tokens into one chronological timeline
  const items = [
    ...s.scans.map((sc) => ({ kind: "scan", at: sc.created_at, ...sc })),
    ...s.tokens.filter((t) => t.status === "pending").map((t) => ({ kind: "pending", at: t.created_at, ...t })),
  ].sort((a, b) => a.at.localeCompare(b.at));

  return (
    <div className="fade-up">
      <div className="flex items-center justify-between mb-6">
        <button onClick={() => navigate("/sessions")} className="flex items-center gap-2 text-sm text-slate-400 hover:text-slate-200">
          <ArrowLeft className="w-4 h-4" /> Back to sessions
        </button>
        {s.scans.length > 0 && (
          <button
            onClick={requestRescan}
            disabled={busy}
            data-testid="session-request-rescan-button"
            className="flex items-center gap-2 bg-amber-500/15 hover:bg-amber-500/25 border border-amber-500/30 text-amber-300 rounded-lg px-3.5 py-2 text-sm disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /> Request Re-scan
          </button>
        )}
      </div>

      <div className="card-soc p-6 mb-6">
        <p className="eyebrow mb-2">INTERVIEW SESSION</p>
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="font-head text-2xl font-extrabold tracking-tight">{s.candidate_name}</h1>
          {s.interview_id && <span className="font-mono text-xs text-slate-400">{s.interview_id}</span>}
          {s.environment_changed ? (
            <span className="flex items-center gap-1.5 text-xs font-mono text-red-400 bg-red-500/10 px-2 py-1 rounded">
              <GitCompareArrows className="w-3.5 h-3.5" /> ENVIRONMENT CHANGED DURING SESSION
            </span>
          ) : s.scans.length > 1 ? (
            <span className="flex items-center gap-1.5 text-xs font-mono text-emerald-400 bg-emerald-500/10 px-2 py-1 rounded">
              <ShieldCheck className="w-3.5 h-3.5" /> ENVIRONMENT STABLE
            </span>
          ) : null}
        </div>
        <div className="text-sm text-slate-500 mt-1">
          {s.candidate_email || "no email"} · {s.scans.length} scan{s.scans.length === 1 ? "" : "s"} · started{" "}
          {new Date(s.created_at).toLocaleString()}
        </div>
      </div>

      <div className="relative pl-8" data-testid="session-timeline">
        <div className="absolute left-[11px] top-2 bottom-2 w-px bg-white/10" />
        {items.map((it, idx) => {
          if (it.kind === "pending") {
            const link = `${window.location.origin}/scan/${it.token}`;
            return (
              <div key={it.id} className="relative mb-5">
                <span className="absolute -left-8 top-3 w-6 h-6 rounded-full bg-[#0F172A] border border-sky-500/50 flex items-center justify-center">
                  <Hourglass className="w-3 h-3 text-sky-400" />
                </span>
                <div className="card-soc p-4" style={{ borderColor: "rgba(56,189,248,0.3)" }} data-testid="timeline-pending">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-2 text-sm">
                      <KeyRound className="w-4 h-4 text-sky-400" />
                      <span className="text-slate-200 font-medium">{it.is_rescan ? "Re-scan requested" : "Baseline scan"} — awaiting candidate</span>
                      <span className="text-xs text-slate-500 font-mono">expires {new Date(it.expires_at).toLocaleDateString()}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <button onClick={() => copy(link, it.id)} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-slate-300" title="Copy invite link">
                        {copied === it.id ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                      </button>
                      <a href={`${API}/tokens/${it.id}/instructions.pdf`} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-slate-300" title="Instructions PDF">
                        <FileText className="w-4 h-4" />
                      </a>
                      <a href={link} className="p-2 rounded-lg bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300" title="Open candidate page">
                        <PlayCircle className="w-4 h-4" />
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            );
          }
          const v = VERDICTS[it.verdict] || VERDICTS.clear;
          const ch = it.changes;
          return (
            <div key={it.id} className="relative mb-5">
              <span
                className="absolute -left-8 top-3 w-6 h-6 rounded-full bg-[#0F172A] border flex items-center justify-center font-mono text-[10px] font-bold"
                style={{ borderColor: v.text, color: v.text }}
              >
                {idx + 1}
              </span>
              <div
                className="card-soc p-4 cursor-pointer hover:bg-white/[0.03] transition-colors"
                onClick={() => navigate(`/scans/${it.id}`)}
                data-testid={`timeline-scan-${it.id}`}
              >
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-slate-200 font-medium">
                      {it.is_rescan ? "Re-scan" : "Baseline scan"}
                    </span>
                    <VerdictBadge verdict={it.verdict} />
                    <span className="font-mono text-sm" style={{ color: v.text }}>{it.risk_score}</span>
                    <span className="text-xs text-slate-500">{it.detection_count} detection{it.detection_count === 1 ? "" : "s"}</span>
                  </div>
                  <span className="text-xs text-slate-500 font-mono">{new Date(it.created_at).toLocaleString()}</span>
                </div>
                {it.detections?.length > 0 && (
                  <div className="text-xs text-slate-400 font-mono mt-2 truncate">
                    {it.detections.map((d) => d.name).join(" · ")}
                  </div>
                )}
                {it.is_rescan && ch && (
                  <div
                    className="mt-3 text-xs rounded-lg px-3 py-2 flex items-start gap-2"
                    style={{ background: ch.changed ? "rgba(239,68,68,0.08)" : "rgba(16,185,129,0.08)" }}
                  >
                    <GitCompareArrows className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: ch.changed ? "#EF4444" : "#10B981" }} />
                    <span className={ch.changed ? "text-red-300" : "text-emerald-300"}>
                      {ch.changed ? (
                        <>
                          Environment changed vs baseline ({ch.risk_delta > 0 ? "+" : ""}{ch.risk_delta} risk).{" "}
                          {[...ch.new_detections.map((d) => d.replace(/^[a-z_]+::/, "")), ...ch.new_overlays, ...ch.new_audio_devices, ...ch.new_camera_devices]
                            .slice(0, 5)
                            .join(", ")}
                        </>
                      ) : (
                        "Consistent with baseline — no new tools, overlays or devices."
                      )}
                    </span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
